import { useEffect, useState } from "react";

export type ChromeStoreReview = {
  id: string;
  author: string;
  avatar?: string;
  rating: number;
  text: string;
  date: string;
  timestamp: number;
};

type ChromeStorePayload = {
  installs: string | null;
  rating: string | null;
  reviewCount: string | null;
  reviews: ChromeStoreReview[];
};

const CACHE_TTL_MS = 5 * 60 * 1000;

type CachedPayload = ChromeStorePayload & { at: number };

const cacheKey = (extensionId: string) => `cws-live-${extensionId}`;

const readCache = (extensionId: string): ChromeStorePayload | null => {
  try {
    const raw = sessionStorage.getItem(cacheKey(extensionId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CachedPayload;
    if (!parsed || Date.now() - parsed.at > CACHE_TTL_MS) return null;
    return {
      installs: parsed.installs ?? null,
      rating: parsed.rating ?? null,
      reviewCount: parsed.reviewCount ?? null,
      reviews: Array.isArray(parsed.reviews) ? parsed.reviews : [],
    };
  } catch {
    return null;
  }
};

const writeCache = (extensionId: string, payload: ChromeStorePayload) => {
  try {
    sessionStorage.setItem(
      cacheKey(extensionId),
      JSON.stringify({ ...payload, at: Date.now() }),
    );
  } catch {
    // Ignore quota / private-mode failures.
  }
};

const emptyPayload = (): ChromeStorePayload => ({
  installs: null,
  rating: null,
  reviewCount: null,
  reviews: [],
});

export const useChromeWebStore = (extensionId?: string) => {
  const [data, setData] = useState<ChromeStorePayload>(emptyPayload);
  const [loading, setLoading] = useState(Boolean(extensionId));

  useEffect(() => {
    if (!extensionId) {
      setData(emptyPayload());
      setLoading(false);
      return;
    }

    const cached = readCache(extensionId);
    if (cached) {
      setData(cached);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);

    fetch(`/api/chrome-web-store?id=${encodeURIComponent(extensionId)}`)
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Chrome Web Store stats failed (${response.status})`);
        }
        return response.json() as Promise<ChromeStorePayload>;
      })
      .then((payload) => {
        if (cancelled) return;
        const next: ChromeStorePayload = {
          installs: payload.installs ?? null,
          rating: payload.rating ?? null,
          reviewCount: payload.reviewCount ?? null,
          reviews: Array.isArray(payload.reviews) ? payload.reviews : [],
        };
        setData(next);
        writeCache(extensionId, next);
      })
      .catch(() => {
        // Keep fallback values from the product page.
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [extensionId]);

  return { ...data, loading };
};
